import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe2, Loader2, RotateCcw, Languages, Plus, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRegional, formatDateRegional } from "@/context/RegionalContext";
import { isTenantAdmin } from "@/lib/roles";
import { currencySymbol } from "@/lib/chartTheme";
import { TERM_KEYS, baseTerm } from "@/lib/i18n";

const FIELDS = ["language", "locale", "currency", "timezone", "date_format", "number_system", "first_day_of_week",
  "fiscal_year_start_month", "week_numbering"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const TZ_HINTS = ["UTC", "Asia/Kolkata", "America/New_York", "America/Chicago", "America/Los_Angeles", "Africa/Nairobi", "Asia/Dubai", "Asia/Riyadh", "Europe/London", "Europe/Paris", "Asia/Singapore"];
const INHERIT = "__inherit__";

function sampleNumber(loc, numberSystem, currency) {
  const n = 12345678.9;
  let body;
  try { body = n.toLocaleString(loc || undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); } catch { body = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  const compact = numberSystem === "indian" ? "1.23Cr" : "12.35M";
  return `${currencySymbol(currency)}${body}  ·  ${compact}`;
}

// One regional form used for both "my preferences" and "tenant defaults" (item 20).
function RegionalForm({ value, onChange, presets, inheritLabel, testid }) {
  const v = value || {};
  const set = (k, x) => onChange({ ...v, [k]: x === INHERIT ? "" : x });
  const sel = (k, opts, w = "w-[180px]") => (
    <Select value={v[k] || INHERIT} onValueChange={(x) => set(k, x)}>
      <SelectTrigger data-testid={`${testid}-${k}`} className={`h-8 text-xs bg-[color:var(--surface-2)] ${w}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={INHERIT}>{inheritLabel}</SelectItem>
        {opts.map(([val, label]) => <SelectItem key={val} value={String(val)}>{label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  const p = presets || {};
  return (
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-3">
      <Row label="Language">{sel("language", (p.languages || []).map((l) => [l.value, `${l.label}${l.rtl ? " · RTL" : ""}`]))}</Row>
      <Row label="Number locale">
        <Input data-testid={`${testid}-locale`} value={v.locale || ""} onChange={(e) => set("locale", e.target.value)} placeholder={inheritLabel} list={`${testid}-locales`} className="h-8 text-xs bg-[color:var(--surface-2)] w-[180px]" />
        <datalist id={`${testid}-locales`}>{["en-IN", "en-US", "en-GB", "en-KE", "ar-AE", "ar-SA", "fr-FR", "sw-KE"].map((l) => <option key={l} value={l} />)}</datalist>
      </Row>
      <Row label="Currency">{sel("currency", (p.currencies || []).map((c) => [c.value, `${c.value} (${c.symbol.trim()})`]))}</Row>
      <Row label="Timezone">
        <Input data-testid={`${testid}-timezone`} value={v.timezone || ""} onChange={(e) => set("timezone", e.target.value)} placeholder={inheritLabel} list={`${testid}-tz`} className="h-8 text-xs bg-[color:var(--surface-2)] w-[180px]" />
        <datalist id={`${testid}-tz`}>{TZ_HINTS.map((z) => <option key={z} value={z} />)}</datalist>
      </Row>
      <Row label="Date format">{sel("date_format", (p.date_formats || []).map((d) => [d, d]))}</Row>
      <Row label="Number system">{sel("number_system", [["intl", "International (1,234,567)"], ["indian", "Indian (12,34,567 · L / Cr)"]])}</Row>
      <Row label="Week starts on">{sel("first_day_of_week", [["1", "Monday"], ["0", "Sunday"], ["6", "Saturday"]])}</Row>
      <Row label="Fiscal year starts">{sel("fiscal_year_start_month", MONTH_NAMES.map((m, i) => [i + 1, m]))}</Row>
      <Row label="Week numbering">{sel("week_numbering", [["iso", "ISO 8601 (Mon, week 1 = first 4-day week)"], ["us", "US / Excel (Sun, week 1 contains Jan 1)"]], "w-[220px]")}</Row>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Label className="text-xs text-[color:var(--ink-3)]">{label}</Label>
      <span className="flex items-center gap-1">{children}</span>
    </div>
  );
}

function Preview({ eff }) {
  const today = formatDateRegional(new Date().toISOString(), eff.date_format || "yyyy-MM-dd", eff.timezone);
  return (
    <div data-testid="regional-preview" dir={eff.rtl ? "rtl" : "ltr"} className="mt-4 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)]/60 px-4 py-3 text-sm text-[color:var(--ink-2)] flex flex-wrap gap-x-6 gap-y-1">
      <span data-testid="regional-preview-number" className="ltr-nums tabular-nums">{sampleNumber(eff.locale, eff.number_system, eff.currency)}</span>
      <span data-testid="regional-preview-date">{today}</span>
      <span data-testid="regional-preview-fiscal" className="text-[color:var(--ink-3)]">FY starts {MONTH_NAMES[(Number(eff.fiscal_year_start_month) || 1) - 1]} · {String(eff.week_numbering || "iso").toUpperCase()} weeks</span>
      <span data-testid="regional-preview-meta" className="text-[color:var(--ink-3)]">{eff.locale} · {eff.currency} · {eff.timezone}{eff.rtl ? " · RTL" : ""}</span>
    </div>
  );
}

export function RegionalCard() {
  const { user } = useAuth();
  const { regional: eff, reload } = useRegional();
  const admin = isTenantAdmin(user?.role);
  const [presets, setPresets] = useState(null);
  const [mine, setMine] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [saving, setSaving] = useState("");
  useEffect(() => {
    api.get("/regional/presets").then((r) => setPresets(r.data)).catch(() => setPresets({}));
    api.get("/regional/effective").then((r) => { setMine(r.data.mine || {}); setTenant(r.data.tenant || {}); }).catch(() => { setMine({}); setTenant({}); });
  }, []);
  const clean = (o) => Object.fromEntries(Object.entries(o || {}).filter(([k, v]) => FIELDS.includes(k) && v !== "" && v != null));
  const saveMine = async () => {
    setSaving("me");
    try { await api.put("/regional/me", clean(mine)); await reload(); toast.success("Your regional preferences were saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save preferences"); } finally { setSaving(""); }
  };
  const resetMine = async () => {
    setSaving("me");
    try { await api.delete("/regional/me"); setMine({}); await reload(); toast.success("Back to the tenant defaults"); }
    catch { toast.error("Could not reset"); } finally { setSaving(""); }
  };
  const saveTenant = async () => {
    setSaving("tenant");
    try { await api.put("/regional/tenant", clean(tenant)); await reload(); toast.success("Tenant regional defaults saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save tenant defaults"); } finally { setSaving(""); }
  };
  // Item 20: workspace (site) layer — tenant default → workspace → user override
  const [sites, setSites] = useState(null);
  const [siteId, setSiteIdSel] = useState("");
  const [siteReg, setSiteReg] = useState(null);
  useEffect(() => { if (admin) api.get("/sites").then((r) => setSites(r.data || [])).catch(() => setSites([])); }, [admin]);
  useEffect(() => {
    if (!siteId) { setSiteReg(null); return; }
    api.get(`/regional/site/${siteId}`).then((r) => setSiteReg(r.data.regional || {})).catch(() => setSiteReg({}));
  }, [siteId]);
  const saveSite = async () => {
    setSaving("site");
    try { await api.put(`/regional/site/${siteId}`, clean(siteReg)); await reload(); toast.success("Workspace regional defaults saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save workspace defaults"); } finally { setSaving(""); }
  };
  const resetSite = async () => {
    setSaving("site");
    try { await api.delete(`/regional/site/${siteId}`); setSiteReg({}); await reload(); toast.success("Workspace follows the tenant defaults"); }
    catch { toast.error("Could not reset"); } finally { setSaving(""); }
  };

  // Item 20: organisation translation dictionary
  const [dictLang, setDictLang] = useState("en");
  const [dict, setDict] = useState(null);
  const [rows, setRows] = useState([]);
  useEffect(() => { if (admin) api.get("/regional/dictionary").then((r) => setDict(r.data.dictionary || {})).catch(() => setDict({})); }, [admin]);
  useEffect(() => { setRows(Object.entries((dict || {})[dictLang] || {}).map(([k, v]) => ({ k, v }))); }, [dict, dictLang]);
  const saveDict = async () => {
    setSaving("dict");
    const terms = Object.fromEntries(rows.filter((r) => r.k && r.v).map((r) => [r.k, r.v]));
    try {
      const { data } = await api.put("/regional/dictionary", { language: dictLang, terms });
      setDict(data.dictionary || {});
      await reload();
      toast.success("Organisation wording saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the dictionary"); } finally { setSaving(""); }
  };

  const applyPreset = (id) => {
    const p = (presets?.presets || []).find((x) => x.id === id);
    if (p) setTenant(Object.fromEntries(FIELDS.map((k) => [k, String(p[k])])));
  };
  return (
    <Card data-testid="regional-card" className="glass-card border-0 p-6">
      <div className="flex items-center gap-2">
        <Globe2 className="w-5 h-5 text-[color:var(--brand-ocean)]" aria-hidden="true" />
        <h3 className="font-display text-lg text-[color:var(--ink)]">Regional settings</h3>
      </div>
      <p className="text-xs text-[color:var(--ink-3)] mt-1">Language, number locale, currency, timezone and date format used across dashboards, tables, exports and AI insights. Your preferences override the tenant defaults; dashboards can still pin their own locale.</p>
      <Preview eff={eff} />

      <div className="mt-5">
        <div className="text-sm font-medium text-[color:var(--ink)]">My preferences</div>
        {mine === null ? <Loader2 className="w-4 h-4 animate-spin mt-2 text-[color:var(--ink-3)]" /> : (
          <RegionalForm value={mine} onChange={setMine} presets={presets} inheritLabel="Use tenant default" testid="regional-me" />
        )}
        <div className="flex gap-2 mt-3">
          <Button data-testid="regional-me-save" size="sm" onClick={saveMine} disabled={saving === "me" || mine === null} className="bg-[color:var(--brand-ocean)] text-white">{saving === "me" && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Save my preferences</Button>
          <Button data-testid="regional-me-reset" size="sm" variant="outline" onClick={resetMine} disabled={saving === "me"}><RotateCcw className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Use tenant defaults</Button>
        </div>
      </div>

      {admin && tenant !== null && (
        <div className="mt-6 pt-5 border-t border-[color:var(--glass-border)]">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-[color:var(--ink)]">Tenant defaults <span className="text-[10px] text-[color:var(--ink-3)] font-normal">(admin)</span></div>
            <Select onValueChange={applyPreset}>
              <SelectTrigger data-testid="regional-tenant-preset" className="h-8 text-xs bg-[color:var(--surface-2)] w-[200px]"><SelectValue placeholder="Apply a region preset…" /></SelectTrigger>
              <SelectContent>{(presets?.presets || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.label} · {p.currency}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <RegionalForm value={tenant} onChange={setTenant} presets={presets} inheritLabel="Platform default" testid="regional-tenant" />
          <div className="flex gap-2 mt-3">
            <Button data-testid="regional-tenant-save" size="sm" onClick={saveTenant} disabled={saving === "tenant"} className="bg-[color:var(--brand-ocean)] text-white">{saving === "tenant" && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Save tenant defaults</Button>
          </div>
        </div>
      )}

      {admin && (
        <div className="mt-6 pt-5 border-t border-[color:var(--glass-border)]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-[color:var(--ink)] flex items-center gap-1.5"><Building2 className="w-4 h-4 text-[color:var(--brand-ocean)]" aria-hidden="true" />Workspace defaults</div>
              <p className="text-[10px] text-[color:var(--ink-3)]">Per site/workspace overrides — applied to every dashboard in that workspace, still overridable per user.</p>
            </div>
            <Select value={siteId} onValueChange={setSiteIdSel}>
              <SelectTrigger data-testid="regional-site-select" className="h-8 text-xs bg-[color:var(--surface-2)] w-[200px]"><SelectValue placeholder="Pick a workspace…" /></SelectTrigger>
              <SelectContent>{(sites || []).map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {siteId && siteReg !== null && (
            <>
              <RegionalForm value={siteReg} onChange={setSiteReg} presets={presets} inheritLabel="Use tenant default" testid="regional-site" />
              <div className="flex gap-2 mt-3">
                <Button data-testid="regional-site-save" size="sm" onClick={saveSite} disabled={saving === "site"} className="bg-[color:var(--brand-ocean)] text-white">{saving === "site" && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Save workspace defaults</Button>
                <Button data-testid="regional-site-reset" size="sm" variant="outline" onClick={resetSite} disabled={saving === "site"}><RotateCcw className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Use tenant defaults</Button>
              </div>
            </>
          )}
        </div>
      )}

      {admin && dict !== null && (
        <div className="mt-6 pt-5 border-t border-[color:var(--glass-border)]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-[color:var(--ink)] flex items-center gap-1.5"><Languages className="w-4 h-4 text-[color:var(--brand-ocean)]" aria-hidden="true" />Organisation wording</div>
              <p className="text-[10px] text-[color:var(--ink-3)]">Replace built-in labels with your own terminology (e.g. “Outlets” instead of “Sites”) per language.</p>
            </div>
            <Select value={dictLang} onValueChange={setDictLang}>
              <SelectTrigger data-testid="regional-dict-lang" className="h-8 text-xs bg-[color:var(--surface-2)] w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>{(presets?.languages || [{ value: "en", label: "English" }]).map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="mt-3 space-y-1.5" data-testid="regional-dict-rows">
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <Select value={r.k} onValueChange={(v) => setRows(rows.map((x, j) => (j === i ? { ...x, k: v } : x)))}>
                  <SelectTrigger data-testid={`regional-dict-key-${i}`} className="h-8 text-xs bg-[color:var(--surface-2)] w-[200px]"><SelectValue placeholder="Pick a label…" /></SelectTrigger>
                  <SelectContent className="max-h-[260px]">{TERM_KEYS.map((k) => <SelectItem key={k} value={k}>{baseTerm(k, "en")}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid={`regional-dict-value-${i}`} value={r.v} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, v: e.target.value } : x)))}
                  placeholder={r.k ? baseTerm(r.k, dictLang) : "Your wording"} className="h-8 text-xs bg-[color:var(--surface-2)] flex-1 max-w-[260px]" />
                <button data-testid={`regional-dict-remove-${i}`} onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Remove term"
                  className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {!rows.length && <p className="text-[11px] text-[color:var(--ink-3)]">No custom wording yet — the built-in labels are used.</p>}
          </div>
          <div className="flex gap-2 mt-3">
            <Button data-testid="regional-dict-add" size="sm" variant="outline" onClick={() => setRows([...rows, { k: "", v: "" }])}><Plus className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Add term</Button>
            <Button data-testid="regional-dict-save" size="sm" onClick={saveDict} disabled={saving === "dict"} className="bg-[color:var(--brand-ocean)] text-white">{saving === "dict" && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Save wording</Button>
          </div>
        </div>
      )}
    </Card>
  );
}
